/**
 * AuthedFetchContext — provides a token-attaching `fetch` to the component tree
 * (task 3.11; Req 3.2, 3.3, 3.4).
 *
 * This module does NOT import from `@viamericas/auth-react` or the adapters
 * layer — it is a pure React context so that components importing it (App.tsx)
 * do not transitively pull in the restricted package at test time. The actual
 * `authedFetch` value is supplied by the provider in `main.tsx`.
 *
 * Components consume it via `useAuthedFetchContext()`, which returns global
 * `fetch` when no provider is mounted (test environments).
 */

import { createContext, useContext, type ReactNode } from "react";

/**
 * Context holding the token-attaching fetch. Defaults to `null` when no
 * provider is mounted (test environment).
 */
const AuthedFetchCtx = createContext<typeof fetch | null>(null);

export interface AuthedFetchProviderProps {
  /** The authed fetch function to expose to the tree. */
  value: typeof fetch;
  children: ReactNode;
}

/**
 * Provider component that exposes a token-attaching fetch to the tree.
 * The value is built externally (in main.tsx) using the auth adapters.
 */
export function AuthedFetchProvider({ value, children }: AuthedFetchProviderProps) {
  return (
    <AuthedFetchCtx.Provider value={value}>
      {children}
    </AuthedFetchCtx.Provider>
  );
}

/**
 * Consume the token-attaching fetch from context. Falls back to global `fetch`
 * when no `AuthedFetchProvider` is mounted (test environments without auth).
 */
export function useAuthedFetchContext(): typeof fetch {
  const fromContext = useContext(AuthedFetchCtx);
  return fromContext ?? fetch;
}
